/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { ROUTES } from "@nf/constants";
import { useCallback, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { ActionsHandler } from "components/ActionsHandler";
import { PaymentDetailType } from "components/PaymentDetailType";
import { useBuildLocalizedAbsolutedUrl, useCurrentSubscription } from "hooks";
import { NoSubscriptionsContainer } from "../NoSubscriptionContainer/NoSubscriptionsContainer";
import { PaymentDetailsText } from "./components";
import { useCreatePaymentDetails } from "./hooks/useCreatePaymentDetails";
import { useGetPaymentDetails } from "./hooks/useGetPaymentDetails";
import SettingsSubscriptionButton from "../SettingsSubscriptionButton";

import styles from "./SettingsPaymentDetails.module.scss";

export const SettingsPaymentDetails = () => {
  const { t } = useTranslation("translations");
  const buildLocalizedAbsoluteUrl = useBuildLocalizedAbsolutedUrl();
  const currentSubscription = useCurrentSubscription();

  const {
    data: currentPaymentDetails,
    error: currentPaymentDetailsError,
    isLoading: isFetchingCurrentPaymentDetails,
    mutate: getPaymentDetails,
  } = useGetPaymentDetails();

  const {
    data: newPaymentDetails,
    isLoading: isCreatingPaymentDetails,
    mutateAsync: createPaymentDetails,
  } = useCreatePaymentDetails();

  const changePaymentDetails = useCallback(() => {
    createPaymentDetails({
      AcceptUrl: buildLocalizedAbsoluteUrl(ROUTES.PAYMENT_UPDATING),
      CancelUrl: buildLocalizedAbsoluteUrl(ROUTES.SETTINGS_PAYMENT_DETAILS),
    }).then((paymentOrder) => {
      if (paymentOrder.CheckoutUrl) {
        window.location.href = paymentOrder.CheckoutUrl;
      }
    });
  }, []);

  useEffect(() => {
    getPaymentDetails();
  }, []);

  return (
    <div>
      <ActionsHandler
        error={
          currentPaymentDetailsError ? currentPaymentDetailsError : undefined
        }
        centeredError
        isLoading={isFetchingCurrentPaymentDetails}
      >
        {currentSubscription ? (
          <>
            <PaymentDetailType subscription={currentSubscription} />
            <div className={styles.paymentDetails}>
              {currentPaymentDetails && (
                <PaymentDetailsText
                  cardType={currentPaymentDetails.CardType}
                  maskedCard={currentPaymentDetails.MaskedCard}
                />
              )}
            </div>
          </>
        ) : (
          <NoSubscriptionsContainer />
        )}
      </ActionsHandler>

      {currentSubscription && !newPaymentDetails && (
        <SettingsSubscriptionButton
          variant="subtle"
          state={isCreatingPaymentDetails ? "loading" : "default"}
          onClick={changePaymentDetails}
        >
          {t("web_payment_details_edit_payment_details")}
        </SettingsSubscriptionButton>
      )}
    </div>
  );
};

export default SettingsPaymentDetails;
